import React, { useState, useEffect, useRef } from 'react';

interface DataPacket {
  id: string;
  type: 'file' | 'metadata' | 'response' | 'auth';
  content: any;
  position: { x: number; y: number };
  targetNode?: string;
  speed: number;
  path: string[];
  currentPathIndex: number;
  isPaused?: boolean;
  color?: string;
}

interface SystemNode {
  id: string;
  name: string;
  type: 'browser' | 'api' | 'worker' | 'database' | 'storage' | 'cdn';
  position: { x: number; y: number };
  status: 'idle' | 'processing' | 'success' | 'error';
  logs: string[];
  currentData?: any;
}

interface Pipe {
  from: string;
  to: string;
  isActive: boolean;
  flowDirection: 'forward' | 'reverse';
  packets: DataPacket[];
}

export default function InteractiveArchitectureExplorer() {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [currentStep, setCurrentStep] = useState('');
  const [logs, setLogs] = useState<string[]>([]);
  const [dataPackets, setDataPackets] = useState<DataPacket[]>([]);
  const [nodes, setNodes] = useState<SystemNode[]>([
    {
      id: 'browser',
      name: 'Your Browser',
      type: 'browser',
      position: { x: 100, y: 300 },
      status: 'idle',
      logs: []
    },
    {
      id: 'cloudflare-edge',
      name: 'CloudFlare Edge',
      type: 'cdn',
      position: { x: 300, y: 300 },
      status: 'idle',
      logs: []
    },
    {
      id: 'pages-function',
      name: 'Pages Function',
      type: 'worker',
      position: { x: 500, y: 200 },
      status: 'idle',
      logs: []
    },
    {
      id: 'worker-api',
      name: 'Worker API',
      type: 'api',
      position: { x: 500, y: 400 },
      status: 'idle',
      logs: []
    },
    {
      id: 'd1-database',
      name: 'D1 Database',
      type: 'database',
      position: { x: 700, y: 200 },
      status: 'idle',
      logs: []
    },
    {
      id: 'r2-storage',
      name: 'R2 Storage',
      type: 'storage',
      position: { x: 700, y: 400 },
      status: 'idle',
      logs: []
    }
  ]);
  const [pipes, setPipes] = useState<Pipe[]>([]);
  const [selectedNode, setSelectedNode] = useState<string | null>(null);
  const [filePreview, setFilePreview] = useState<string>('');
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();

  // File handling
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);

      // Create preview for images
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setFilePreview(e.target?.result as string);
        };
        reader.readAsDataURL(file);
      }

      addLog(`📁 File selected: ${file.name} (${(file.size / 1024).toFixed(2)} KB)`);
    }
  };

  // Logging system
  const addLog = (message: string) => {
    setLogs(prev => [...prev, `[${new Date().toLocaleTimeString()}] ${message}`]);
  };

  // Start the upload flow
  const startUploadFlow = async () => {
    if (!selectedFile) return;

    setIsRunning(true);
    setIsPaused(false);
    setDataPackets([]);

    // Step 1: File to FormData conversion in browser
    await animateStep('browser', async () => {
      addLog('🔄 Converting file to FormData...');
      updateNodeStatus('browser', 'processing');

      // Create visual data packet
      const packet: DataPacket = {
        id: 'file-packet',
        type: 'file',
        content: { name: selectedFile.name, size: selectedFile.size },
        position: { x: 100, y: 300 },
        targetNode: 'cloudflare-edge',
        speed: speed,
        path: ['browser', 'cloudflare-edge', 'worker-api', 'r2-storage'],
        currentPathIndex: 0,
        color: '#3B82F6'
      };

      setDataPackets([packet]);
      await movePacket(packet);
    });

    // Step 2: CloudFlare Edge authentication
    await animateStep('cloudflare-edge', async () => {
      addLog('🔐 CloudFlare Edge: Authenticating request...');
      updateNodeStatus('cloudflare-edge', 'processing');

      // Show auth token flow
      const authPacket: DataPacket = {
        id: 'auth-packet',
        type: 'auth',
        content: { token: 'CF-Ray: 7x9y8z...' },
        position: nodes[1].position,
        targetNode: 'worker-api',
        speed: speed,
        path: ['cloudflare-edge', 'worker-api'],
        currentPathIndex: 0,
        color: '#10B981'
      };

      setDataPackets(prev => [...prev, authPacket]);
    });

    // Step 3: Worker API processing
    await animateStep('worker-api', async () => {
      addLog('⚙️ Worker API: Processing upload request...');
      updateNodeStatus('worker-api', 'processing');

      // Show data transformation
      addLog('🔄 Generating R2 storage key: business/{slug}/{filename}');

      // Create metadata packet to D1
      const metaPacket: DataPacket = {
        id: 'metadata-packet',
        type: 'metadata',
        content: { filename: selectedFile.name, timestamp: Date.now() },
        position: nodes[3].position,
        targetNode: 'd1-database',
        speed: speed,
        path: ['worker-api', 'd1-database'],
        currentPathIndex: 0,
        color: '#8B5CF6'
      };

      setDataPackets(prev => [...prev, metaPacket]);
    });

    // Step 4: Store in R2
    await animateStep('r2-storage', async () => {
      addLog('💾 R2 Storage: Storing file...');
      updateNodeStatus('r2-storage', 'processing');

      // Show success response flowing back
      const responsePacket: DataPacket = {
        id: 'response-packet',
        type: 'response',
        content: { success: true, url: 'https://...' },
        position: nodes[5].position,
        targetNode: 'browser',
        speed: speed * 2,
        path: ['r2-storage', 'worker-api', 'cloudflare-edge', 'browser'],
        currentPathIndex: 0,
        color: '#22C55E'
      };

      setDataPackets(prev => [...prev, responsePacket]);
    });

    // Complete
    updateNodeStatus('browser', 'success');
    addLog('✅ Upload complete! File stored successfully.');
    setIsRunning(false);
  };

  // Animation helpers
  const animateStep = async (nodeId: string, action: () => Promise<void>) => {
    if (isPaused) {
      await waitForUnpause();
    }

    setCurrentStep(nodeId);
    updateNodeStatus(nodeId, 'processing');
    await action();
    await sleep(1000 / speed);
    updateNodeStatus(nodeId, 'idle');
  };

  const movePacket = async (packet: DataPacket) => {
    const steps = 20;
    for (let i = 0; i <= steps; i++) {
      if (isPaused) await waitForUnpause();

      const progress = i / steps;
      const currentNode = nodes.find(n => n.id === packet.path[packet.currentPathIndex]);
      const targetNode = nodes.find(n => n.id === packet.path[packet.currentPathIndex + 1]);

      if (currentNode && targetNode) {
        packet.position = {
          x: currentNode.position.x + (targetNode.position.x - currentNode.position.x) * progress,
          y: currentNode.position.y + (targetNode.position.y - currentNode.position.y) * progress
        };

        setDataPackets(prev => [...prev]);
        await sleep(50 / speed);
      }
    }

    packet.currentPathIndex++;
  };

  const waitForUnpause = () => {
    return new Promise<void>(resolve => {
      const checkPause = setInterval(() => {
        if (!isPaused) {
          clearInterval(checkPause);
          resolve();
        }
      }, 100);
    });
  };

  const updateNodeStatus = (nodeId: string, status: 'idle' | 'processing' | 'success' | 'error') => {
    setNodes(prev => prev.map(node =>
      node.id === nodeId ? { ...node, status } : node
    ));
  };

  const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

  // Canvas drawing for pipes and data flow
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Draw pipes
      nodes.forEach((node, i) => {
        nodes.forEach((targetNode, j) => {
          if (i < j && Math.abs(i - j) === 1) {
            ctx.beginPath();
            ctx.moveTo(node.position.x + 50, node.position.y + 25);
            ctx.lineTo(targetNode.position.x + 50, targetNode.position.y + 25);
            ctx.strokeStyle = '#E5E7EB';
            ctx.lineWidth = 4;
            ctx.stroke();

            // Animated flow effect
            if (isRunning) {
              ctx.setLineDash([10, 10]);
              ctx.lineDashOffset = -Date.now() / 50;
              ctx.strokeStyle = '#3B82F6';
              ctx.lineWidth = 2;
              ctx.stroke();
              ctx.setLineDash([]);
            }
          }
        });
      });

      // Draw data packets
      dataPackets.forEach(packet => {
        ctx.beginPath();
        ctx.arc(packet.position.x + 50, packet.position.y + 25, 8, 0, Math.PI * 2);
        ctx.fillStyle = packet.color || '#3B82F6';
        ctx.fill();

        // Pulse effect
        const pulse = Math.sin(Date.now() / 200) * 0.3 + 0.7;
        ctx.globalAlpha = pulse;
        ctx.beginPath();
        ctx.arc(packet.position.x + 50, packet.position.y + 25, 12, 0, Math.PI * 2);
        ctx.strokeStyle = packet.color || '#3B82F6';
        ctx.lineWidth = 2;
        ctx.stroke();
        ctx.globalAlpha = 1;
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [nodes, dataPackets, isRunning]);

  const getNodeIcon = (type: string) => {
    switch (type) {
      case 'browser': return '🌐';
      case 'cdn': return '🛡️';
      case 'worker': return '⚙️';
      case 'api': return '🔌';
      case 'database': return '🗄️';
      case 'storage': return '📦';
      default: return '📍';
    }
  };

  const getNodeColor = (status: string) => {
    switch (status) {
      case 'processing': return 'bg-yellow-500 animate-pulse';
      case 'success': return 'bg-green-500';
      case 'error': return 'bg-red-500';
      default: return 'bg-gray-400';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 text-white p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-5xl font-bold mb-4 bg-gradient-to-r from-blue-400 to-purple-600 bg-clip-text text-transparent">
            Interactive Architecture Explorer
          </h1>
          <p className="text-xl text-gray-300">
            Upload a real file and watch it flow through the system
          </p>
        </div>

        {/* Control Panel */}
        <div className="bg-slate-800 rounded-xl p-6 mb-8 shadow-2xl">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* File Input */}
            <div>
              <label className="block text-sm font-medium mb-2">Select File</label>
              <input
                type="file"
                onChange={handleFileSelect}
                className="w-full px-4 py-2 bg-slate-700 rounded-lg text-white file:mr-4 file:py-1 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-500 file:text-white hover:file:bg-blue-600"
                disabled={isRunning}
              />
              {filePreview && (
                <img src={filePreview} alt="Preview" className="mt-2 h-20 rounded" />
              )}
            </div>

            {/* Speed Control */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Speed: {speed}x
              </label>
              <input
                type="range"
                min="0.5"
                max="3"
                step="0.5"
                value={speed}
                onChange={(e) => setSpeed(parseFloat(e.target.value))}
                className="w-full"
                disabled={isRunning}
              />
            </div>

            {/* Controls */}
            <div className="flex items-end gap-2">
              <button
                onClick={startUploadFlow}
                disabled={!selectedFile || isRunning}
                className="px-6 py-2 bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
              >
                {isRunning ? 'Running...' : 'Start Upload'}
              </button>
              <button
                onClick={() => setIsPaused(!isPaused)}
                disabled={!isRunning}
                className="px-6 py-2 bg-yellow-600 rounded-lg hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
              >
                {isPaused ? 'Resume' : 'Pause'}
              </button>
              <button
                onClick={() => {
                  setIsRunning(false);
                  setDataPackets([]);
                  setNodes(prev => prev.map(n => ({ ...n, status: 'idle' })));
                }}
                className="px-6 py-2 bg-red-600 rounded-lg hover:bg-red-700 font-semibold"
              >
                Reset
              </button>
            </div>
          </div>
        </div>

        {/* Main Visualization Area */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* System Diagram */}
          <div className="lg:col-span-2 bg-slate-800 rounded-xl p-6 shadow-2xl relative">
            <h2 className="text-2xl font-bold mb-4">System Architecture</h2>

            {/* Canvas for pipes and animations */}
            <canvas
              ref={canvasRef}
              width={800}
              height={500}
              className="absolute top-0 left-0 pointer-events-none"
            />

            {/* Nodes */}
            <div className="relative" style={{ height: '500px' }}>
              {nodes.map(node => (
                <div
                  key={node.id}
                  className={`absolute transform -translate-x-1/2 -translate-y-1/2 cursor-pointer transition-all hover:scale-110`}
                  style={{
                    left: `${node.position.x}px`,
                    top: `${node.position.y}px`,
                    zIndex: selectedNode === node.id ? 20 : 10
                  }}
                  onClick={() => setSelectedNode(node.id)}
                >
                  <div className={`relative ${currentStep === node.id ? 'animate-bounce' : ''}`}>
                    <div className={`w-24 h-24 rounded-2xl flex flex-col items-center justify-center ${getNodeColor(node.status)} shadow-lg`}>
                      <span className="text-3xl">{getNodeIcon(node.type)}</span>
                      <span className="text-xs mt-1 font-semibold">{node.name}</span>
                    </div>
                    {node.status === 'processing' && (
                      <div className="absolute -inset-2 rounded-2xl bg-yellow-400 opacity-30 animate-ping" />
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Legend */}
            <div className="absolute bottom-4 left-4 bg-slate-900/80 rounded-lg p-3">
              <div className="text-xs space-y-1">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-blue-500 rounded-full" />
                  <span>File Data</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full" />
                  <span>Auth Token</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-purple-500 rounded-full" />
                  <span>Metadata</span>
                </div>
              </div>
            </div>
          </div>

          {/* Logs & Inspector */}
          <div className="space-y-4">
            {/* Node Inspector */}
            {selectedNode && (
              <div className="bg-slate-800 rounded-xl p-4 shadow-2xl">
                <h3 className="text-lg font-bold mb-2">
                  Node Inspector: {nodes.find(n => n.id === selectedNode)?.name}
                </h3>
                <div className="text-sm space-y-2">
                  <div>
                    <span className="text-gray-400">Status:</span>{' '}
                    <span className={`font-semibold ${
                      nodes.find(n => n.id === selectedNode)?.status === 'processing' ? 'text-yellow-400' :
                      nodes.find(n => n.id === selectedNode)?.status === 'success' ? 'text-green-400' :
                      'text-gray-300'
                    }`}>
                      {nodes.find(n => n.id === selectedNode)?.status}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-400">Type:</span>{' '}
                    <span>{nodes.find(n => n.id === selectedNode)?.type}</span>
                  </div>
                  {selectedNode === 'r2-storage' && selectedFile && (
                    <div>
                      <span className="text-gray-400">Stored as:</span>{' '}
                      <code className="text-xs bg-slate-700 px-1 py-0.5 rounded">
                        business/xyz123/test/{selectedFile.name}
                      </code>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Live Logs */}
            <div className="bg-slate-800 rounded-xl p-4 shadow-2xl">
              <h3 className="text-lg font-bold mb-2">Live System Logs</h3>
              <div className="h-64 overflow-y-auto bg-slate-900 rounded p-3 font-mono text-xs">
                {logs.length === 0 ? (
                  <div className="text-gray-500">Waiting for activity...</div>
                ) : (
                  logs.map((log, i) => (
                    <div key={i} className="text-green-400 mb-1">
                      {log}
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Data Packet Inspector */}
            {dataPackets.length > 0 && (
              <div className="bg-slate-800 rounded-xl p-4 shadow-2xl">
                <h3 className="text-lg font-bold mb-2">Active Data Packets</h3>
                <div className="space-y-2">
                  {dataPackets.map(packet => (
                    <div key={packet.id} className="bg-slate-700 rounded p-2 text-sm">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold">{packet.type}</span>
                        <span className="text-xs text-gray-400">
                          {packet.path[packet.currentPathIndex]} → {packet.path[packet.currentPathIndex + 1]}
                        </span>
                      </div>
                      <pre className="text-xs mt-1 text-gray-300">
                        {JSON.stringify(packet.content, null, 2)}
                      </pre>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Educational Tips */}
        <div className="mt-8 bg-slate-800/50 rounded-xl p-6 border border-slate-700">
          <h3 className="text-xl font-bold mb-3">🎓 What's Happening?</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <h4 className="font-semibold text-blue-400 mb-1">1. Client-Side Processing</h4>
              <p className="text-gray-300">Your browser converts the file into binary data and wraps it in a FormData object for transmission.</p>
            </div>
            <div>
              <h4 className="font-semibold text-green-400 mb-1">2. Edge Authentication</h4>
              <p className="text-gray-300">CloudFlare's edge network validates the request and adds security headers before forwarding.</p>
            </div>
            <div>
              <h4 className="font-semibold text-purple-400 mb-1">3. Distributed Storage</h4>
              <p className="text-gray-300">Files are stored in R2 (object storage) while metadata goes to D1 (SQL database) for fast queries.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}